import React, { useEffect } from 'react';
import { getTabbableCandidates } from '../../../hooks/use-roving-focus/get-tabbable-candidates';
import { focus } from '../../../hooks/use-roving-focus/use-roving-focus';
import { Optional } from '../../../utils/typescriptUtils';
import { Button, ButtonProps } from '../button/button';
import { useView } from './context';

interface ViewProps {
  id: string;
  children: React.ReactNode;
}

export function View({ id, children }: ViewProps) {
  const { container, view } = useView();

  useEffect(() => {
    // Focus the first tabbable element when the view is active
    if (container.current && view === id) {
      const candidates = getTabbableCandidates(container.current);
      focus(candidates[0]);
    }
  }, [container, id, view]);

  return view === id ? <div ref={container}>{children}</div> : null;
}

export function DefaultView({ children }: Omit<ViewProps, 'id'>) {
  const { container, view } = useView();
  return view === undefined ? <div ref={container}>{children}</div> : null;
}

interface ViewButtonProps extends Optional<ButtonProps, 'onClick'> {
  id: string;
}

export function ViewButton({ id, children, onClick, ...rest }: ViewButtonProps) {
  const { setView } = useView();

  return (
    <Button
      {...rest}
      onClick={() => {
        setView(id);
        onClick?.();
      }}
    >
      {children}
    </Button>
  );
}

interface ViewBackButtonProps extends Optional<ButtonProps, 'onClick'> {}

export function ViewBackButton({ children, onClick, ...rest }: ViewBackButtonProps) {
  const { removeView } = useView();
  return (
    <Button
      {...rest}
      onClick={() => {
        removeView();
        onClick?.();
      }}
    >
      {children}
    </Button>
  );
}

View.Button = ViewButton;
View.BackButton = ViewBackButton;
View.View = View;
View.DefaultView = DefaultView;
